import cn from 'classnames'
import { cta_buttonPrimaryText } from 'ext-ui/style/colors'
import React, {
  ChangeEvent,
  CSSProperties,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import ChipIcon from 'src/assets/images/marcom-bin-card/chip.svg'

import style from './BinCard.style'

export interface BinCardBankProfile {
  brandColors?: {
    from: string
    to: string
  }
  cardArt?: string
  cardNetwork?: string
}

const EXTEND_MARK_LOGO_URL =
  'https://brand-assets.paywithextend.com/extend/MarkLogoKnockout.svg'
const IDLE_GRADIENT =
  'linear-gradient(237.75deg, rgb(1, 208, 180) 0%, rgb(68, 137, 249) 100%)'
const ARROW_GRADIENT = {
  blue: '#2563eb',
  teal: '#0f766e',
  gradient: 'linear-gradient(90deg, #2563eb 0%, #0f766e 100%)',
}
const BIN_CARD_PROMPT =
  'Find out if you qualify for discounted pricing with your company card'
const BIN_CARD_PRIVACY_COPY =
  'Only your first 6 digits are used — your full number is never stored.'
const BIN_CARD_NO_MATCH_COPY =
  'Your bank does not qualify for discounted pricing.'
const BIN_CARD_ERROR_COPY = 'Something went wrong - try again in a moment.'
const BUSINESS_CARD_LABEL = 'Business'
const CARD_LABEL = 'Card'
const PARTNER_LABEL = 'Partner'
const PRICING_LABEL = 'Pricing'
const USE_DIFFERENT_CARD_LABEL = 'Use a different card'

const CLEAR_ICON = (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M9 1L1 9M1 1l8 8"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

export type BinMatchTreatment = '1' | '2' | '3' | '5'

interface BinCardProps {
  onBinChange: (bin: string) => void
  bank: BinCardBankProfile | null
  autoSubmit?: boolean
  savingsLabel?: string | null
  matchTreatment?: BinMatchTreatment
  defaultValue?: string
  autoFocusInput?: boolean
  forceHover?: boolean
  hasError?: boolean
  isLoading?: boolean
}

export default function BinCard(props: BinCardProps): ReactElement {
  const {
    onBinChange,
    bank,
    autoSubmit = true,
    matchTreatment = '2',
    defaultValue = '',
    autoFocusInput = false,
    forceHover = false,
    hasError = false,
    isLoading = false,
  } = props
  const [value, setValue] = useState(defaultValue)
  const [focused, setFocused] = useState(autoFocusInput)
  const [hovered, setHovered] = useState(forceHover)
  const [isResolving, setIsResolving] = useState(false)
  const [matchKey, setMatchKey] = useState(0)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [artA, setArtA] = useState<string | null>(null)
  const [artB, setArtB] = useState<string | null>(null)
  const [activeSlot, setActiveSlot] = useState<'a' | 'b'>('a')
  const prevBankRef = useRef<BinCardBankProfile | null>(null)
  const prevCardArtRef = useRef<string | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)

  const showError = hasError && value.length === 6

  useEffect(() => {
    if (autoFocusInput) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [autoFocusInput])

  useEffect(() => {
    const newArt = bank?.cardArt ?? null
    const oldArt = prevCardArtRef.current ?? null

    if (newArt !== oldArt) {
      if (activeSlot === 'a') {
        setArtB(newArt)
        setActiveSlot('b')
      } else {
        setArtA(newArt)
        setActiveSlot('a')
      }
    }

    prevCardArtRef.current = bank?.cardArt
  }, [bank?.cardArt])

  useEffect(() => {
    if (bank && !prevBankRef.current) {
      prevBankRef.current = bank

      if (bank.cardArt) {
        setIsResolving(true)

        let live = true
        const safetyTimer = window.setTimeout(() => {
          if (live) {
            setIsResolving(false)
          }
        }, 3000)
        const image = new Image()

        image.onload = image.onerror = () => {
          if (live) {
            setIsResolving(false)
          }
          window.clearTimeout(safetyTimer)
        }
        image.src = bank.cardArt

        return () => {
          live = false
          window.clearTimeout(safetyTimer)
        }
      }
    }

    if (!bank) {
      prevBankRef.current = null
    }

    return undefined
  }, [bank])

  const isMatched = bank !== null && !isResolving && !showError

  useEffect(() => {
    if (!isMatched) return undefined

    setMatchKey((currentMatchKey) => currentMatchKey + 1)
    setHasScrolled(false)

    let onScroll: (() => void) | null = null
    const listenTimer = window.setTimeout(() => {
      onScroll = () => setHasScrolled(true)
      window.addEventListener('scroll', onScroll, {
        passive: true,
        once: true,
      })
    }, 1800)

    return () => {
      window.clearTimeout(listenTimer)
      if (onScroll) {
        window.removeEventListener('scroll', onScroll)
      }
    }
  }, [isMatched])

  useEffect(() => {
    if (value === '') {
      onBinChange('')
    }
  }, [onBinChange, value])

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 6)

    setValue(digits)

    if (digits.length === 6) {
      onBinChange(digits)
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' && !autoSubmit) {
      onBinChange(value)
    }
  }

  function handleReset(): void {
    setValue('')
    onBinChange('')
    prevBankRef.current = null

    requestAnimationFrame(() => inputRef.current?.focus())
  }

  function focusInput(): void {
    if (!isMatched) {
      inputRef.current?.focus()
    }
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      focusInput()
    }
  }

  const showNoMatch = value.length === 6 && !bank && !showError && !isLoading
  const showStatusMessage = showNoMatch || showError
  const isPopulated = !focused && value.length > 0
  const inputWrapperClassName = focused
    ? 'is-focused'
    : hovered && !isPopulated
      ? 'is-hovered'
      : isPopulated
        ? 'is-populated'
        : 'is-idle'
  const inputTextClassName =
    focused || isPopulated ? 'is-darkText' : 'is-lightText'
  const showInlineClearButton = matchTreatment === '3' || matchTreatment === '5'
  const cardNumberRowTop = matchTreatment === '5' ? '50%' : 'calc(50% + 37px)'
  const cardBackground =
    isMatched && bank?.brandColors
      ? `linear-gradient(237.75deg, ${bank.brandColors.from} 0%, ${bank.brandColors.to} 100%)`
      : IDLE_GRADIENT
  const cardAnimation = isMatched
    ? hasScrolled
      ? 'marcom-bin-card-arrow-enter 900ms ease-out 650ms both'
      : 'marcom-bin-card-arrow-enter 900ms ease-out 650ms both, marcom-bin-card-arrow-bounce 1s ease-in-out 1.65s infinite'
    : showStatusMessage
      ? 'marcom-bin-card-arrow-enter 900ms ease-out 300ms both'
      : undefined
  const descriptionStyle: CSSProperties = {
    opacity: isMatched ? 0 : 1,
    transform: isMatched ? 'translateY(-10px)' : 'translateY(0)',
    transition:
      'opacity 320ms ease, transform 320ms cubic-bezier(0.16, 1, 0.3, 1)',
    pointerEvents: isMatched ? 'none' : 'auto',
    backgroundImage: ARROW_GRADIENT.gradient,
  }
  const cardArtStyle: CSSProperties = {
    opacity: isMatched ? 1 : 0,
    transition: 'opacity 700ms ease-in',
    willChange: 'opacity',
  }
  const cardLayerStyle: CSSProperties = {
    opacity: isMatched ? 0 : 1,
    transition: 'opacity 700ms ease-in',
    pointerEvents: isMatched ? 'none' : 'auto',
  }
  const matchedLayerStyle: CSSProperties = {
    opacity: isMatched ? 1 : 0,
    transition: 'opacity 700ms ease-in',
    pointerEvents: isMatched ? 'auto' : 'none',
  }
  const scrimStyle: CSSProperties = {
    opacity: isMatched ? 1 : 0,
    transition: 'opacity 700ms ease-in',
    background:
      'radial-gradient(ellipse 55% 70px at 91% 66%, rgba(0,0,0,0.5) 0%, transparent 100%)',
  }
  const privacyStyle: CSSProperties = {
    opacity: showStatusMessage ? 0 : 1,
    transition: 'opacity 400ms ease',
  }
  const statusMessageStyle: CSSProperties = {
    opacity: showStatusMessage ? 1 : 0,
    transition: 'opacity 400ms ease',
    zIndex: 5,
  }
  const floatingClearButtonStyle: CSSProperties = {
    opacity: isMatched || showStatusMessage ? 1 : 0,
    pointerEvents: isMatched || showStatusMessage ? 'auto' : 'none',
    transition: 'opacity 700ms ease-in, background-color 150ms',
  }
  const differentCardLinkWrapperStyle: CSSProperties = {
    height: 20,
    display: 'flex',
    alignItems: 'center',
    opacity: isMatched ? 1 : 0,
    transition: 'opacity 400ms ease',
    transitionDelay: isMatched ? '700ms' : '0ms',
    pointerEvents: isMatched ? 'auto' : 'none',
  }
  const arrowStyle: CSSProperties = cardAnimation
    ? { animation: cardAnimation }
    : { opacity: 0 }
  const arrowLabelStyle: CSSProperties = {
    opacity: isMatched || showStatusMessage ? 1 : 0,
    transitionDelay: isMatched ? '700ms' : showStatusMessage ? '400ms' : '0ms',
  }

  const isAmex = bank?.cardNetwork === 'AMEX'
  const displayNumber = isAmex
    ? `${value.slice(0, 4).padEnd(4, '•')}  ${value.slice(4, 6).padEnd(6, '•')}  ${'•••••'}`
    : `${value.slice(0, 4).padEnd(4, '•')}  ${value.slice(4, 6).padEnd(2, '•')}••  ••••  ••••`

  return (
    <div className="MarcomBinCard" css={style}>
      <div className="MarcomBinCard-root">
        <div className="MarcomBinCard-description">
          <p className="MarcomBinCard-descriptionText" style={descriptionStyle}>
            {BIN_CARD_PROMPT}
          </p>
        </div>

        <div className="MarcomBinCard-cardSection">
          <div className="MarcomBinCard-cardWrapper">
            <div
              data-testid="bin-card"
              className={cn('MarcomBinCard-card', {
                'is-hovered': hovered && !isMatched,
              })}
              style={{ background: cardBackground }}
              onMouseEnter={() => !forceHover && setHovered(true)}
              onMouseLeave={() => !forceHover && setHovered(false)}
              onClick={focusInput}
              onKeyDown={handleCardKeyDown}
              role="button"
              tabIndex={0}
            >
              {artA && (
                <img
                  src={artA}
                  alt=""
                  className="MarcomBinCard-cardArt"
                  decoding="async"
                  style={{
                    ...cardArtStyle,
                    opacity: isMatched && activeSlot === 'a' ? 1 : 0,
                    transition: 'opacity 300ms ease',
                  }}
                />
              )}
              {artB && (
                <img
                  src={artB}
                  alt=""
                  className="MarcomBinCard-cardArt"
                  decoding="async"
                  style={{
                    ...cardArtStyle,
                    opacity: isMatched && activeSlot === 'b' ? 1 : 0,
                    transition: 'opacity 300ms ease',
                  }}
                />
              )}

              {matchTreatment === '3' && (
                <div className="MarcomBinCard-scrim" style={scrimStyle} />
              )}

              <div className="MarcomBinCard-gloss" />

              {showError && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background: 'rgba(10,15,35,0.42)',
                    opacity: showError ? 1 : 0,
                    transition: 'opacity 300ms ease',
                    zIndex: 4,
                  }}
                />
              )}

              <div className="MarcomBinCard-layer" style={cardLayerStyle}>
                <div className="MarcomBinCard-topRow">
                  <img src={ChipIcon} alt="" className="MarcomBinCard-chip" />
                  <img
                    alt="Extend"
                    className="MarcomBinCard-extendIcon"
                    src={EXTEND_MARK_LOGO_URL}
                  />
                </div>

                <p className="MarcomBinCard-privacyNote" style={privacyStyle}>
                  <svg
                    width="10"
                    height="12"
                    viewBox="0 0 10 12"
                    fill="none"
                    aria-hidden="true"
                    className="MarcomBinCard-lockIcon"
                  >
                    <rect
                      x="1"
                      y="5"
                      width="8"
                      height="6.5"
                      rx="1.2"
                      stroke="white"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M3 5V3.5a2 2 0 0 1 4 0V5"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {BIN_CARD_PRIVACY_COPY}
                </p>

                <p
                  className="MarcomBinCard-noMatchMessage"
                  style={statusMessageStyle}
                  role={showError ? 'alert' : undefined}
                >
                  {showError && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      {...props}
                    >
                      <g fill={cta_buttonPrimaryText} fillRule="nonzero">
                        <path d="M12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25 2.25 6.615 2.25 12s4.365 9.75 9.75 9.75Zm0-1.5a8.25 8.25 0 1 1 0-16.5 8.25 8.25 0 0 1 0 16.5Z" />
                        <path d="M11.165 7.01a.75.75 0 0 1 1.5 0v7.175a.75.75 0 0 1-1.5 0V7.01Zm0 9.992a.75.75 0 0 1 1.5 0v.423a.75.75 0 0 1-1.5 0v-.423Z" />
                      </g>
                    </svg>
                  )}
                  {showError ? BIN_CARD_ERROR_COPY : BIN_CARD_NO_MATCH_COPY}
                </p>

                <div
                  className={cn(
                    'MarcomBinCard-inputWrapper',
                    inputWrapperClassName,
                  )}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                      if (!bank) {
                        setFocused(false)
                      }
                    }}
                    placeholder="Enter first 6 digits"
                    maxLength={6}
                    aria-label="Enter first 6 digits of your business card"
                    className={cn('MarcomBinCard-input', inputTextClassName)}
                  />
                  {value.length > 0 && (
                    <span
                      className={cn(
                        'MarcomBinCard-counter',
                        inputTextClassName,
                      )}
                    >
                      {`${value.length}/6`}
                    </span>
                  )}
                </div>

                <div className="MarcomBinCard-cardTypeLabel">
                  <p>{BUSINESS_CARD_LABEL}</p>
                  <p>{CARD_LABEL}</p>
                </div>
              </div>

              <div className="MarcomBinCard-layer" style={matchedLayerStyle}>
                <div
                  className={cn('MarcomBinCard-cardNumberRow', {
                    withInlineClear: showInlineClearButton,
                    withoutInlineClear: !showInlineClearButton,
                  })}
                  style={{ top: cardNumberRowTop }}
                >
                  <p className="MarcomBinCard-cardNumber">{displayNumber}</p>
                  {showInlineClearButton && (
                    <button
                      type="button"
                      className="MarcomBinCard-inlineClearButton"
                      onClick={handleReset}
                      aria-label="Clear card"
                    >
                      {CLEAR_ICON}
                    </button>
                  )}
                </div>

                <div className="MarcomBinCard-partnerPricingLabel">
                  <p>{PARTNER_LABEL}</p>
                  <p>{PRICING_LABEL}</p>
                </div>
              </div>
            </div>

            {matchTreatment === '2' && (
              <button
                type="button"
                className="MarcomBinCard-floatingClearButton"
                onClick={handleReset}
                aria-label="Clear card"
                style={floatingClearButtonStyle}
              >
                {CLEAR_ICON}
              </button>
            )}
          </div>

          {matchTreatment === '1' && (
            <div style={differentCardLinkWrapperStyle}>
              <button
                type="button"
                onClick={handleReset}
                className="MarcomBinCard-differentCardLink"
              >
                {USE_DIFFERENT_CARD_LABEL}
              </button>
            </div>
          )}

          <div className="MarcomBinCard-arrowSection">
            <svg
              key={matchKey}
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              style={arrowStyle}
            >
              <defs>
                <linearGradient
                  id="marcom-bin-card-arrow-gradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={ARROW_GRADIENT.blue} />
                  <stop offset="100%" stopColor={ARROW_GRADIENT.teal} />
                </linearGradient>
              </defs>
              <path
                d="M12 5v14M5 12l7 7 7-7"
                stroke="url(#marcom-bin-card-arrow-gradient)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="MarcomBinCard-arrowLabel" style={arrowLabelStyle}>
              {isMatched
                ? 'See your pricing below'
                : 'Save 25% with annual billing'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
